/**
 * Direct S3 Upload Module
 * 
 * Enables direct browser-to-S3 uploads using presigned URLs,
 * supporting bulk uploads of hundreds of files with progress tracking.
 */

(function($, window) {
    'use strict';

    // File status constants
    const FileStatus = {
        PENDING: 'pending',
        GETTING_URL: 'getting_url',
        UPLOADING: 'uploading',
        COMPLETE: 'complete',
        ERROR: 'error',
        CANCELLED: 'cancelled'
    };

    /**
     * DirectS3Uploader Class
     * Manages batch uploads directly to S3 with concurrency control
     */
    class DirectS3Uploader {
        constructor(options = {}) {
            this.concurrency = options.concurrency || 5;
            this.maxRetries = options.maxRetries || 3;
            this.batchSize = options.batchSize || 50; // URLs to request at a time
            this.ajaxUrl = options.ajaxUrl || (typeof evidenceData !== 'undefined' ? evidenceData.ajax_url : '/wp-admin/admin-ajax.php');
            this.nonce = options.nonce || (typeof evidenceData !== 'undefined' ? evidenceData.nonce : '');
            
            // Callbacks
            this.onFileProgress = options.onFileProgress || function() {};
            this.onFileComplete = options.onFileComplete || function() {};
            this.onFileError = options.onFileError || function() {};
            this.onAllComplete = options.onAllComplete || function() {};
            this.onOverallProgress = options.onOverallProgress || function() {};
            
            // State
            this.files = [];
            this.uploadQueue = [];
            this.activeUploads = 0;
            this.isUploading = false;
            this.isCancelled = false;
            this.evidenceId = null;
            this.isNewEvidence = false;
            
            // Statistics
            this.stats = {
                total: 0,
                completed: 0,
                failed: 0,
                cancelled: 0,
                totalBytes: 0,
                uploadedBytes: 0
            };
            
            // Track active XHR requests for cancellation
            this.activeXHRs = new Map();
        }

        /**
         * Initialize upload with file list
         * @param {FileList|Array} files - Files to upload
         * @param {string} evidenceId - Evidence ID (or 'new' for new evidence)
         */
        async uploadFiles(files, evidenceId = 'new') {
            this.files = Array.from(files);
            this.evidenceId = evidenceId;
            this.isNewEvidence = (evidenceId === 'new');
            this.isCancelled = false;
            this.isUploading = true;
            
            // Initialize stats
            this.stats = {
                total: this.files.length,
                completed: 0,
                failed: 0,
                cancelled: 0,
                totalBytes: this.files.reduce((sum, f) => sum + f.size, 0),
                uploadedBytes: 0
            };
            
            // Create file upload state objects
            this.uploadQueue = this.files.map((file, index) => ({
                index: index,
                file: file,
                status: FileStatus.PENDING,
                progress: 0,
                retries: 0,
                presignedData: null,
                error: null,
                uploadedBytes: 0
            }));
            
            console.log(`DirectS3Uploader: Starting upload of ${this.files.length} files`);
            
            try {
                // Get presigned URLs in batches
                await this.getPresignedUrls();
                
                if (this.isCancelled) {
                    return this.getResults();
                }
                
                // Start concurrent uploads
                await this.processUploadQueue();
                
                // Register uploaded files in database
                if (!this.isCancelled) {
                    await this.registerUploadedFiles();
                }
                
            } catch (error) {
                console.error('DirectS3Uploader: Upload error', error);
            }
            
            this.isUploading = false;
            const results = this.getResults();
            this.onAllComplete(results);
            return results;
        }

        /**
         * Get presigned URLs for all files in batches
         */
        async getPresignedUrls() {
            const batches = this.chunkArray(this.uploadQueue, this.batchSize);
            
            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                if (this.isCancelled) break;
                
                const batch = batches[batchIndex];
                
                // Mark files as getting URL
                batch.forEach(item => {
                    item.status = FileStatus.GETTING_URL;
                    this.onFileProgress(item.index, item);
                });
                
                const filesData = batch.map(item => ({
                    name: item.file.name,
                    type: item.file.type || 'application/octet-stream',
                    size: item.file.size
                }));
                
                try {
                    const response = await this.ajaxRequest('get_presigned_upload_urls', {
                        evidence_id: this.evidenceId,
                        files: JSON.stringify(filesData)
                    });
                    
                    if (response.success) {
                        // Update evidence ID if this is new evidence
                        if (this.isNewEvidence && response.data.evidence_id) {
                            this.evidenceId = response.data.evidence_id;
                            this.isNewEvidence = false;
                        }
                        
                        // Assign presigned data to queue items
                        response.data.files.forEach((fileData, i) => {
                            const queueItem = batch[i];
                            if (fileData.success) {
                                queueItem.presignedData = fileData;
                                queueItem.status = FileStatus.PENDING;
                            } else {
                                queueItem.status = FileStatus.ERROR;
                                queueItem.error = fileData.error || 'Failed to get upload URL';
                                this.stats.failed++;
                                this.onFileError(queueItem.index, queueItem);
                            }
                            this.onFileProgress(queueItem.index, queueItem);
                        });
                    } else {
                        // Mark all files in batch as failed
                        batch.forEach(item => {
                            item.status = FileStatus.ERROR;
                            item.error = response.data?.message || 'Failed to get upload URLs';
                            this.stats.failed++;
                            this.onFileError(item.index, item);
                        });
                    }
                } catch (error) {
                    console.error('Error getting presigned URLs:', error);
                    batch.forEach(item => {
                        item.status = FileStatus.ERROR;
                        item.error = 'Network error getting upload URL';
                        this.stats.failed++;
                        this.onFileError(item.index, item);
                    });
                }
            }
        }

        /**
         * Process the upload queue with concurrency control
         */
        async processUploadQueue() {
            const pendingItems = this.uploadQueue.filter(
                item => item.status === FileStatus.PENDING && item.presignedData
            );
            
            // Create a pool of concurrent upload promises
            const uploadPromises = [];
            let currentIndex = 0;
            
            const uploadNext = async () => {
                while (currentIndex < pendingItems.length && !this.isCancelled) {
                    if (this.activeUploads >= this.concurrency) {
                        // Wait for a slot to become available
                        await new Promise(resolve => setTimeout(resolve, 100));
                        continue;
                    }
                    
                    const item = pendingItems[currentIndex++];
                    if (item && item.status === FileStatus.PENDING) {
                        this.activeUploads++;
                        this.uploadFile(item).finally(() => {
                            this.activeUploads--;
                        });
                    }
                }
            };
            
            // Start concurrent uploaders
            const workers = [];
            for (let i = 0; i < this.concurrency; i++) {
                workers.push(uploadNext());
            }
            
            await Promise.all(workers);
            
            // Wait for all active uploads to complete
            while (this.activeUploads > 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        /**
         * Upload a single file to S3
         */
        async uploadFile(item) {
            return new Promise((resolve) => {
                if (this.isCancelled) {
                    item.status = FileStatus.CANCELLED;
                    this.stats.cancelled++;
                    resolve();
                    return;
                }
                
                item.status = FileStatus.UPLOADING;
                this.onFileProgress(item.index, item);
                
                const xhr = new XMLHttpRequest();
                this.activeXHRs.set(item.index, xhr);
                
                // Progress tracking
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const previousBytes = item.uploadedBytes;
                        item.uploadedBytes = event.loaded;
                        item.progress = Math.round((event.loaded / event.total) * 100);
                        
                        // Update overall stats
                        this.stats.uploadedBytes += (item.uploadedBytes - previousBytes);
                        
                        this.onFileProgress(item.index, item);
                        this.updateOverallProgress();
                    }
                };
                
                xhr.onload = () => {
                    this.activeXHRs.delete(item.index);
                    
                    if (xhr.status >= 200 && xhr.status < 300) {
                        item.status = FileStatus.COMPLETE;
                        item.progress = 100;
                        item.uploadedBytes = item.file.size;
                        this.stats.completed++;
                        this.onFileComplete(item.index, item);
                    } else {
                        this.handleUploadError(item, `Upload failed with status ${xhr.status}`, resolve);
                        return;
                    }
                    
                    this.updateOverallProgress();
                    resolve();
                };
                
                xhr.onerror = () => {
                    this.activeXHRs.delete(item.index);
                    this.handleUploadError(item, 'Network error during upload', resolve);
                };
                
                xhr.onabort = () => {
                    this.activeXHRs.delete(item.index);
                    item.status = FileStatus.CANCELLED;
                    this.stats.cancelled++;
                    this.onFileProgress(item.index, item);
                    resolve();
                };
                
                // Send PUT request directly to S3
                xhr.open('PUT', item.presignedData.upload_url, true);
                xhr.setRequestHeader('Content-Type', item.presignedData.file_type || item.file.type);
                xhr.send(item.file);
            });
        }

        /**
         * Handle upload error with retry logic
         */
        handleUploadError(item, errorMessage, resolve) {
            item.retries++;
            
            if (item.retries <= this.maxRetries && !this.isCancelled) {
                console.log(`Retrying upload for ${item.file.name} (attempt ${item.retries}/${this.maxRetries})`);
                item.status = FileStatus.PENDING;
                item.progress = 0;
                item.uploadedBytes = 0;
                this.onFileProgress(item.index, item);
                
                // Retry after a delay
                setTimeout(() => {
                    this.uploadFile(item).then(resolve);
                }, 1000 * item.retries); // Exponential backoff
            } else {
                item.status = FileStatus.ERROR;
                item.error = errorMessage;
                this.stats.failed++;
                this.onFileError(item.index, item);
                this.updateOverallProgress();
                resolve();
            }
        }

        /**
         * Register successfully uploaded files in the database
         */
        async registerUploadedFiles() {
            const completedFiles = this.uploadQueue.filter(
                item => item.status === FileStatus.COMPLETE && item.presignedData
            );
            
            if (completedFiles.length === 0) {
                return;
            }
            
            const filesData = completedFiles.map(item => ({
                file_id: item.presignedData.file_id,
                s3_key: item.presignedData.s3_key,
                name: item.presignedData.original_name || item.file.name,
                type: item.presignedData.file_type || item.file.type
            }));
            
            try {
                const response = await this.ajaxRequest('register_uploaded_files', {
                    evidence_id: this.evidenceId,
                    files: JSON.stringify(filesData)
                });
                
                if (!response.success) {
                    console.error('Failed to register uploaded files:', response.data?.message);
                }
            } catch (error) {
                console.error('Error registering uploaded files:', error);
            }
        }

        /**
         * Update overall progress callback
         */
        updateOverallProgress() {
            const progress = {
                total: this.stats.total,
                completed: this.stats.completed,
                failed: this.stats.failed,
                cancelled: this.stats.cancelled,
                inProgress: this.uploadQueue.filter(i => i.status === FileStatus.UPLOADING).length,
                pending: this.uploadQueue.filter(i => i.status === FileStatus.PENDING).length,
                totalBytes: this.stats.totalBytes,
                uploadedBytes: this.stats.uploadedBytes,
                percentComplete: this.stats.totalBytes > 0 
                    ? Math.round((this.stats.uploadedBytes / this.stats.totalBytes) * 100) 
                    : 0
            };
            
            this.onOverallProgress(progress);
        }

        /**
         * Cancel all uploads
         */
        cancelAll() {
            this.isCancelled = true;
            
            // Abort all active XHR requests
            this.activeXHRs.forEach((xhr, index) => {
                xhr.abort();
            });
            this.activeXHRs.clear();
            
            // Mark pending items as cancelled
            this.uploadQueue.forEach(item => {
                if (item.status === FileStatus.PENDING || item.status === FileStatus.UPLOADING) {
                    item.status = FileStatus.CANCELLED;
                    this.stats.cancelled++;
                    this.onFileProgress(item.index, item);
                }
            });
            
            this.updateOverallProgress();
        }

        /**
         * Cancel a specific file upload
         */
        cancelFile(index) {
            const item = this.uploadQueue[index];
            if (!item) return;
            
            if (item.status === FileStatus.UPLOADING) {
                const xhr = this.activeXHRs.get(index);
                if (xhr) {
                    xhr.abort();
                }
            }
            
            if (item.status === FileStatus.PENDING || item.status === FileStatus.UPLOADING) {
                item.status = FileStatus.CANCELLED;
                this.stats.cancelled++;
                this.onFileProgress(index, item);
                this.updateOverallProgress();
            }
        }

        /**
         * Retry a failed file upload
         */
        async retryFile(index) {
            const item = this.uploadQueue[index];
            if (!item || item.status !== FileStatus.ERROR) return;
            
            item.status = FileStatus.PENDING;
            item.progress = 0;
            item.uploadedBytes = 0;
            item.error = null;
            item.retries = 0;
            this.stats.failed--;
            
            this.onFileProgress(index, item);
            
            // If we don't have presigned data, get a new URL
            if (!item.presignedData) {
                try {
                    const response = await this.ajaxRequest('get_single_presigned_url', {
                        evidence_id: this.evidenceId,
                        file_name: item.file.name,
                        file_type: item.file.type || 'application/octet-stream',
                        file_size: item.file.size
                    });
                    
                    if (response.success) {
                        item.presignedData = response.data;
                    } else {
                        item.status = FileStatus.ERROR;
                        item.error = response.data?.message || 'Failed to get upload URL';
                        this.stats.failed++;
                        this.onFileError(index, item);
                        return;
                    }
                } catch (error) {
                    item.status = FileStatus.ERROR;
                    item.error = 'Network error';
                    this.stats.failed++;
                    this.onFileError(index, item);
                    return;
                }
            }
            
            await this.uploadFile(item);
            
            // Register the file if upload succeeded
            if (item.status === FileStatus.COMPLETE) {
                await this.registerUploadedFiles();
            }
        }

        /**
         * Get upload results
         */
        getResults() {
            return {
                evidenceId: this.evidenceId,
                total: this.stats.total,
                completed: this.stats.completed,
                failed: this.stats.failed,
                cancelled: this.stats.cancelled,
                files: this.uploadQueue.map(item => ({
                    name: item.file.name,
                    status: item.status,
                    fileId: item.presignedData?.file_id,
                    error: item.error
                }))
            };
        }

        /**
         * Get the evidence ID (useful for new evidence)
         */
        getEvidenceId() {
            return this.evidenceId;
        }

        /**
         * Make AJAX request to WordPress
         */
        ajaxRequest(action, data) {
            return new Promise((resolve, reject) => {
                $.ajax({
                    url: this.ajaxUrl,
                    type: 'POST',
                    data: {
                        action: action,
                        nonce: this.nonce,
                        ...data
                    },
                    success: resolve,
                    error: (xhr, status, error) => reject(new Error(error))
                });
            });
        }

        /**
         * Chunk array into smaller arrays
         */
        chunkArray(array, size) {
            const chunks = [];
            for (let i = 0; i < array.length; i += size) {
                chunks.push(array.slice(i, i + size));
            }
            return chunks;
        }
    }

    // Export to window
    window.DirectS3Uploader = DirectS3Uploader;
    window.FileUploadStatus = FileStatus;

})(jQuery, window);
